import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Bell, Mail, MessageSquare, Plus, Trash2, 
  AlertTriangle, DollarSign, TrendingUp, Clock,
  Send, CheckCircle, XCircle, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_TYPES = {
  budget_threshold: { label: 'Budget Threshold', icon: DollarSign, color: 'amber' },
  anomaly_detected: { label: 'Anomaly Detected', icon: AlertTriangle, color: 'rose' },
  cost_spike: { label: 'Cost Spike', icon: TrendingUp, color: 'orange' },
  daily_summary: { label: 'Daily Summary', icon: Clock, color: 'blue' },
};

const CostAlerts = () => {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('rules');
  const [testingChannel, setTestingChannel] = useState(null);

  useEffect(() => {
    if (open) {
      fetchRules();
      fetchHistory();
    }
  }, [open]);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/alerts/rules`);
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch alert rules:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/alerts/history?limit=20`);
      setHistory(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch alert history:', error);
    }
  };

  const testAlert = async (channel) => {
    setTestingChannel(channel);
    try {
      const response = await axios.post(`${API_URL}/alerts/test?channel=${channel}`);
      toast.success(`Test ${channel} alert sent (demo mode)`);
      fetchHistory();
    } catch (error) {
      toast.error(`Failed to send test alert`);
    } finally {
      setTestingChannel(null);
    }
  };

  const triggerAlertCheck = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/alerts/trigger`);
      toast.success(`Triggered ${response.data.triggered} alerts (demo mode)`);
      fetchHistory();
    } catch (error) {
      toast.error('Failed to trigger alerts');
    } finally {
      setLoading(false);
    }
  };

  const toggleRule = async (ruleId, enabled) => {
    // In a real implementation, this would update the rule
    setRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, enabled } : r
    ));
    toast.success(enabled ? 'Alert enabled' : 'Alert disabled');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getAlertTypeConfig = (type) => {
    return ALERT_TYPES[type] || { label: type, icon: Bell, color: 'slate' };
  };

  const getStatusBadge = (status) => {
    if (status === 'demo_mode') {
      return <Badge className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-400 text-[11px]">Demo</Badge>;
    }
    if (status === 'sent') {
      return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400 text-[11px]">Sent</Badge>;
    }
    return <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400 text-[11px]">Failed</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400"
          data-testid="cost-alerts-btn"
        >
          <Bell className="w-4 h-4" />
          <span className="hidden sm:inline">Alerts</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="cost-alerts-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            <DialogTitle>Cost Alerts</DialogTitle>
            <Badge className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-400 text-[11px]">
              Demo Mode
            </Badge>
          </div>
          <DialogDescription className="flex items-center gap-1 text-xs">
            <Info className="w-3 h-3" />
            Alerts are logged but not actually sent in demo mode
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
          <Button
            variant={activeTab === 'rules' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('rules')}
            className={activeTab === 'rules' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            Alert Rules
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            History ({history.length})
          </Button>
          <Button
            variant={activeTab === 'test' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('test')}
            className={activeTab === 'test' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            Test
          </Button>
        </div>

        {/* Rules Tab */}
        {activeTab === 'rules' && (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {rules.map((rule) => {
                const config = getAlertTypeConfig(rule.type);
                const IconComponent = config.icon;
                
                return (
                  <div
                    key={rule.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      rule.enabled 
                        ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                        : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-${config.color}-100 dark:bg-${config.color}-900/30`}>
                          <IconComponent className={`w-4 h-4 text-${config.color}-600`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{rule.name}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{config.label}</p>
                          <div className="flex items-center gap-2 mt-2">
                            {rule.channels?.includes('email') && (
                              <Badge variant="outline" className="text-[11px] gap-1">
                                <Mail className="w-3 h-3" /> Email
                              </Badge>
                            )}
                            {rule.channels?.includes('slack') && (
                              <Badge variant="outline" className="text-[11px] gap-1">
                                <MessageSquare className="w-3 h-3" /> Slack
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={(checked) => toggleRule(rule.id, checked)}
                      />
                    </div>
                    {rule.threshold && (
                      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <p className="text-xs text-slate-400">
                          Threshold: ${rule.threshold.toLocaleString()} 
                          {rule.threshold_percent && ` (${rule.threshold_percent}%)`}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {history.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No alerts yet</p>
                </div>
              ) : (
                history.map((alert, idx) => {
                  const config = getAlertTypeConfig(alert.alert_type);
                  const IconComponent = config.icon;
                  
                  return (
                    <div
                      key={alert.id || idx}
                      className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-1.5 rounded-lg bg-${config.color}-100 dark:bg-${config.color}-900/30`}>
                          <IconComponent className={`w-3 h-3 text-${config.color}-600`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-sm truncate">{alert.rule_name}</p>
                            {getStatusBadge(alert.status)}
                          </div>
                          <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
                            {alert.message}
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
                            <span>{formatTimestamp(alert.sent_at)}</span>
                            <span>•</span>
                            <span>{alert.channels?.join(', ')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        )}

        {/* Test Tab */}
        {activeTab === 'test' && (
          <div className="space-y-4 py-4">
            <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
              <p className="text-sm text-cyan-700 dark:text-cyan-400 font-medium mb-1">Demo Mode Active</p>
              <p className="text-xs text-cyan-600 dark:text-cyan-500">
                Test alerts will be logged but not actually sent. In production, configure your Slack webhook URL and Resend API key in the backend environment.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card className="border-sky-200 dark:border-sky-800">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-sky-600" />
                    <CardTitle className="text-sm">Email Alert</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => testAlert('email')}
                    disabled={testingChannel === 'email'}
                    className="w-full bg-sky-600 hover:bg-sky-700"
                    size="sm"
                  >
                    {testingChannel === 'email' ? (
                      <>Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Test Email</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-emerald-200 dark:border-emerald-800">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-emerald-600" />
                    <CardTitle className="text-sm">Slack Alert</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => testAlert('slack')}
                    disabled={testingChannel === 'slack'}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    size="sm"
                  >
                    {testingChannel === 'slack' ? (
                      <>Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Test Slack</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                onClick={triggerAlertCheck}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                {loading ? 'Checking...' : 'Trigger Alert Check (Simulate Real Alerts)'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CostAlerts;
